import {
  Activity,
  Anvil,
  Bell,
  Bird,
  BookOpen,
  BookText,
  Bot,
  Bug,
  Building2,
  CheckSquare,
  CreditCard,
  DollarSign,
  FileText,
  FlaskConical,
  FolderKanban,
  Home,
  Inbox,
  Key,
  LifeBuoy,
  Lightbulb,
  ListTree,
  MessageCircle,
  Monitor,
  Moon,
  Pencil,
  Percent,
  Play,
  Plus,
  ScrollText,
  Settings,
  Shield,
  Sun,
  Table,
  Tags,
  Terminal,
  ToggleLeft,
  TrendingUp,
  UserCog,
  UserPlus,
  Users,
  Workflow,
} from "lucide-react";
import type { FrontendFeatureFlag } from "~/server/featureFlag/frontendFeatureFlags";
import type { Command } from "./types";

/**
 * Static navigation commands that map to main app routes.
 */
export const navigationCommands: Command[] = [
  // Main pages
  {
    id: "nav-home",
    label: "Home",
    description: "Project home",
    icon: Home,
    category: "navigation",
    keywords: ["dashboard", "start", "main"],
    path: "/[project]",
  },
  {
    id: "nav-analytics",
    label: "Analytics",
    description: "Analytics dashboard",
    icon: TrendingUp,
    category: "navigation",
    keywords: ["metrics", "stats", "charts", "data"],
    path: "/[project]/analytics",
  },
  {
    id: "nav-traces-v2",
    label: "Trace Explorer",
    description: "Explore traces and spans",
    icon: Bird,
    category: "navigation",
    keywords: [
      "traces",
      "trace explorer",
      "explorer",
      "messages",
      "logs",
      "requests",
      "history",
      // Carried over from the removed "Traces (Legacy)" entry so operators
      // who still search by the old name land on the Trace Explorer.
      "legacy",
      "old",
    ],
    path: "/[project]/traces",
  },
  {
    id: "nav-agent-testing",
    label: "Agent Testing",
    description: "Test cases and their results",
    icon: FlaskConical,
    category: "navigation",
    keywords: ["test", "cases", "agent", "simulation", "scenario", "run"],
    path: "/[project]/agent-testing",
    featureFlag: {
      flag: "release_ui_agent_testing_v2_enabled",
      enabled: true,
    },
  },
  {
    id: "nav-simulations",
    label: "Simulations",
    description: "Simulation runs",
    icon: Play,
    category: "navigation",
    keywords: ["test", "run", "execute"],
    path: "/[project]/simulations",
    // Agent Testing replaces this destination. The two lead to the same runs
    // by different routes, so exactly one of them is offered.
    featureFlag: {
      flag: "release_ui_agent_testing_v2_enabled",
      enabled: false,
    },
  },
  {
    id: "nav-scenarios",
    label: "Scenarios",
    description: "Simulations → Scenarios",
    icon: FlaskConical,
    category: "navigation",
    keywords: ["test", "simulation", "scenario"],
    path: "/[project]/simulations/scenarios",
    featureFlag: {
      flag: "release_ui_agent_testing_v2_enabled",
      enabled: false,
    },
  },
  {
    id: "nav-online-evaluations",
    label: "Online Evaluations",
    description: "Configure production evaluations and guardrails",
    icon: CheckSquare,
    category: "navigation",
    keywords: ["eval", "monitor", "guardrail", "production", "check"],
    path: "/[project]/online-evaluations",
  },
  {
    id: "nav-experiments",
    label: "Experiments",
    description: "View experiments",
    icon: FlaskConical,
    category: "navigation",
    keywords: ["experiment", "test", "ab"],
    path: "/[project]/experiments",
  },
  {
    id: "nav-annotations",
    label: "Annotations",
    description: "Annotation queues",
    icon: Pencil,
    category: "navigation",
    keywords: ["label", "tag", "mark", "note"],
    path: "/[project]/annotations",
  },
  {
    id: "nav-annotations-all",
    label: "All Annotations",
    description: "Annotations → All",
    icon: Pencil,
    category: "navigation",
    keywords: ["label", "tag", "all"],
    path: "/[project]/annotations/all",
  },
  {
    id: "nav-annotations-inbox",
    label: "My Annotation Inbox",
    description: "Annotations → Inbox",
    icon: Inbox,
    category: "navigation",
    keywords: ["label", "inbox", "my"],
    path: "/[project]/annotations/me",
  },
  {
    id: "nav-annotations-queue",
    label: "My Annotation Queue",
    description: "Annotations → My Queue",
    icon: ListTree,
    category: "navigation",
    keywords: ["label", "queue", "my"],
    path: "/[project]/annotations/my-queue",
  },
  {
    id: "nav-prompts",
    label: "Prompts",
    description: "Manage prompts",
    icon: BookText,
    category: "navigation",
    keywords: ["template", "prompt", "text", "message"],
    path: "/[project]/prompts",
  },
  {
    id: "nav-agents",
    label: "Agents",
    description: "Manage agents",
    icon: Bot,
    category: "navigation",
    keywords: ["ai", "assistant", "bot", "automation"],
    path: "/[project]/agents",
  },
  {
    id: "nav-workflows",
    label: "Workflows",
    description: "Manage workflows",
    icon: Workflow,
    category: "navigation",
    keywords: ["flow", "pipeline", "process", "automation"],
    path: "/[project]/workflows",
  },
  {
    id: "nav-evaluators",
    label: "Evaluators",
    description: "Manage evaluators",
    icon: Percent,
    category: "navigation",
    keywords: ["eval", "scorer", "judge", "metric"],
    path: "/[project]/evaluators",
  },
  {
    id: "nav-datasets",
    label: "Datasets",
    description: "Manage datasets",
    icon: Table,
    category: "navigation",
    keywords: ["data", "records", "table", "collection"],
    path: "/[project]/datasets",
  },
  {
    id: "nav-automations",
    label: "Automations",
    description: "Manage automations",
    icon: Bell,
    category: "navigation",
    keywords: ["alert", "notification", "trigger", "automation"],
    path: "/[project]/automations",
  },

  // Ops
  {
    id: "nav-ops",
    label: "Ops Dashboard",
    description: "Operations dashboard",
    icon: Terminal,
    category: "navigation",
    keywords: ["ops", "operations", "skynet", "metrics", "throughput"],
    path: "/ops",
  },
  {
    id: "nav-ops-queues",
    label: "Ops Pipelines",
    description: "Ops → Pipelines",
    icon: Terminal,
    category: "navigation",
    keywords: ["ops", "queues", "pipelines", "groups", "redis", "pending"],
    path: "/ops",
  },
  {
    id: "nav-ops-blocked",
    label: "Ops Blocked",
    description: "Ops → Blocked groups",
    icon: Terminal,
    category: "navigation",
    keywords: ["ops", "blocked", "errors", "stuck", "failed"],
    path: "/ops",
  },
  {
    id: "nav-ops-dlq",
    label: "Ops Dead Letters",
    description: "Ops → Dead-letter queue",
    icon: Terminal,
    category: "navigation",
    keywords: ["ops", "dlq", "dead", "letter", "queue", "redrive"],
    path: "/ops",
  },
  {
    id: "nav-ops-process-dead-letters",
    label: "Process Dead Letters",
    description: "Ops → Event Sourcing → Dead Letters",
    icon: Terminal,
    category: "navigation",
    keywords: ["ops", "dead", "letter", "process", "outbox", "intent", "stuck"],
    path: "/ops/event-sourcing/dead-letters",
  },
  {
    id: "nav-ops-projections",
    label: "Projection Replay",
    description: "Ops → Event Sourcing → Projections",
    icon: Terminal,
    category: "navigation",
    keywords: ["ops", "projections", "replay", "event", "sourcing"],
    path: "/ops/event-sourcing/projections",
  },
  {
    id: "nav-ops-dejaview",
    label: "Deja View",
    description: "Ops → Deja View",
    icon: Terminal,
    category: "navigation",
    keywords: [
      "ops",
      "dejaview",
      "deja",
      "view",
      "events",
      "aggregate",
      "replay",
      "debug",
      "inspect",
    ],
    path: "/ops/dejaview",
  },
  {
    id: "nav-ops-foundry",
    label: "The Foundry",
    description: "Ops → The Foundry",
    icon: Terminal,
    category: "navigation",
    keywords: [
      "ops",
      "foundry",
      "forge",
      "trace",
      "test",
      "otel",
      "playground",
      "send",
    ],
    path: "/ops/foundry",
  },

  // Settings pages
  {
    id: "nav-settings",
    label: "Settings",
    description: "Organization settings",
    icon: Settings,
    category: "navigation",
    keywords: ["config", "preferences", "options", "configure"],
    path: "/settings",
  },
  // Members, Teams & Projects and Access became tabs of Directory. Each keeps
  // a command of its own, pointed at the tab it became: somebody typing
  // "members" is looking for a list of people, not for a page called
  // something else, and a command bar that answers with nothing teaches them
  // the feature is gone.
  {
    id: "nav-settings-members",
    label: "Members",
    description: "Settings → Directory → People",
    icon: Users,
    category: "navigation",
    keywords: ["users", "team", "people", "invite", "member", "directory"],
    path: "/settings/directory",
  },
  {
    id: "nav-settings-teams",
    label: "Teams",
    description: "Settings → Directory → Teams & projects",
    icon: Building2,
    category: "navigation",
    keywords: ["team", "group", "department", "directory"],
    path: "/settings/directory?tab=teams",
  },
  {
    id: "nav-settings-projects",
    label: "Projects",
    description: "Settings → Directory → Teams & projects",
    icon: FolderKanban,
    category: "navigation",
    keywords: ["project", "workspace", "directory"],
    path: "/settings/directory?tab=teams",
  },
  {
    id: "nav-settings-groups",
    label: "Groups",
    description: "Settings → Directory → Groups",
    icon: Users,
    category: "navigation",
    keywords: ["group", "directory", "scim", "role assignment"],
    path: "/settings/directory?tab=groups",
  },
  {
    id: "nav-settings-roles",
    label: "Roles",
    description: "Settings → Roles",
    icon: UserCog,
    category: "navigation",
    keywords: ["role", "permission", "access"],
    path: "/settings/roles",
  },
  {
    id: "nav-settings-model-providers",
    label: "Model Providers",
    description: "Settings → Model Providers",
    icon: Key,
    category: "navigation",
    keywords: ["api", "key", "openai", "anthropic", "provider", "llm"],
    path: "/settings/model-providers",
  },
  {
    id: "nav-settings-model-costs",
    label: "Model Costs",
    description: "Settings → Model Costs",
    icon: DollarSign,
    category: "navigation",
    keywords: ["cost", "price", "billing", "model"],
    path: "/settings/model-costs",
  },
  {
    id: "nav-settings-annotation-scores",
    label: "Annotation Scores",
    description: "Settings → Annotation Scores",
    icon: Tags,
    category: "navigation",
    keywords: ["annotation", "score", "label"],
    path: "/settings/annotation-scores",
  },
  {
    id: "nav-settings-topic-clustering",
    label: "Topic Clustering",
    description: "Settings → Topic Clustering",
    icon: Tags,
    category: "navigation",
    keywords: ["topic", "cluster", "group"],
    path: "/settings/topic-clustering",
  },
  {
    id: "nav-settings-usage",
    label: "Usage",
    description: "Settings → Usage",
    icon: TrendingUp,
    category: "navigation",
    keywords: ["usage", "billing", "quota"],
    path: "/settings/usage",
  },
  {
    id: "nav-settings-subscription",
    label: "Subscription",
    description: "Settings → Subscription",
    icon: CreditCard,
    category: "navigation",
    keywords: ["subscription", "billing", "plan", "payment"],
    path: "/settings/subscription",
  },
  {
    id: "nav-settings-plans",
    label: "Plans",
    description: "Settings → Plans",
    icon: CreditCard,
    category: "navigation",
    keywords: ["plans", "pricing", "compare", "billing"],
    path: "/settings/plans",
  },
  {
    id: "nav-settings-authentication",
    label: "Authentication",
    description:
      "Settings → Authentication — single sign-on for your organization",
    icon: Shield,
    category: "navigation",
    keywords: [
      "authentication",
      "sso",
      "single sign-on",
      "saml",
      "oidc",
      "identity provider",
      "okta",
      "entra",
      "google workspace",
      "domain",
      "verify",
      // The second-factor requirement moved here from Access: it is a
      // condition of signing in, so it is asked with the sign-in it guards.
      "two-step",
      "mfa",
      "second factor",
    ],
    path: "/settings/authentication",
  },
  {
    id: "nav-settings-directory",
    label: "Directory",
    description: "Settings → Directory — who is here, and how they got here",
    icon: Users,
    category: "navigation",
    keywords: [
      "directory",
      "people",
      "members",
      "teams",
      "groups",
      "identity provider",
    ],
    path: "/settings/directory",
  },
  {
    id: "nav-settings-provisioning",
    label: "Provisioning",
    description:
      "Settings → Authentication → Connectors — SCIM sync and its tokens",
    icon: Users,
    category: "navigation",
    keywords: [
      "scim",
      "provisioning",
      "sync",
      "tokens",
      "deprovision",
      "identity provider",
    ],
    path: "/settings/authentication/connectors",
  },
  {
    id: "nav-settings-access",
    label: "Joining",
    description: "Settings → Directory → who may join without an invitation",
    icon: UserPlus,
    category: "navigation",
    keywords: [
      "access",
      "join",
      "joining",
      "join requests",
      "domain",
      "auto-join",
      "policy",
    ],
    path: "/settings/directory",
  },
  {
    id: "nav-settings-api-keys",
    label: "API Keys",
    description: "Settings → API Keys",
    icon: Key,
    category: "navigation",
    keywords: ["api key", "token", "access token", "ingestion", "credentials"],
    path: "/settings/api-keys",
  },
  {
    id: "nav-settings-security",
    label: "Security",
    description: "Settings → Security — your own sign-in methods",
    icon: Shield,
    category: "navigation",
    // "sso" and "authentication" deliberately live on the ORGANIZATION
    // authentication entry above: this page is the person's own methods,
    // and the collision sent administrators hunting for SSO to the wrong
    // screen.
    keywords: [
      "security",
      "login",
      "password",
      "passkey",
      "two-factor",
      "two-step",
      "backup codes",
      "linked accounts",
    ],
    path: "/settings/security",
  },
  {
    id: "nav-settings-profile",
    label: "Profile",
    description: "Settings → Profile",
    icon: UserCog,
    category: "navigation",
    keywords: ["profile", "name", "photo", "avatar"],
    path: "/settings/profile",
  },
  {
    id: "nav-settings-audit-log",
    label: "Audit Log",
    description: "Settings → Audit Log",
    icon: ScrollText,
    category: "navigation",
    keywords: ["audit", "log", "history", "activity"],
    path: "/settings/audit-log",
  },
  {
    id: "nav-settings-license",
    label: "License",
    description: "Settings → License",
    icon: FileText,
    category: "navigation",
    keywords: ["license", "key", "activation"],
    path: "/settings/license",
  },
];

/**
 * Action commands that trigger create/edit flows.
 */
export const actionCommands: Command[] = [
  {
    id: "action-new-agent",
    label: "New Agent",
    description: "Create a new agent",
    icon: Plus,
    category: "actions",
    keywords: ["create", "add", "agent", "bot"],
  },
  {
    id: "action-new-evaluation",
    label: "New Evaluation",
    description: "Create a new evaluation",
    icon: Plus,
    category: "actions",
    keywords: ["create", "add", "eval", "test"],
  },
  {
    id: "action-new-prompt",
    label: "New Prompt",
    description: "Create a new prompt",
    icon: Plus,
    category: "actions",
    keywords: ["create", "add", "prompt", "template"],
  },
  {
    id: "action-new-dataset",
    label: "New Dataset",
    description: "Create a new dataset",
    icon: Plus,
    category: "actions",
    keywords: ["create", "add", "data", "records"],
  },
  {
    id: "action-new-automation",
    label: "New Automation",
    description: "Create a notification or action triggered by trace filters",
    icon: Bell,
    category: "actions",
    keywords: [
      "create",
      "add",
      "automation",
      "trigger",
      "alert",
      "slack",
      "email",
    ],
  },
  {
    id: "action-new-scenario",
    label: "New Scenario",
    description: "Create a new scenario",
    icon: Plus,
    category: "actions",
    keywords: ["create", "add", "scenario", "test"],
  },
  {
    id: "action-send-trace",
    label: "Forge a Trace",
    description: "Open The Foundry to craft and send test traces",
    icon: Anvil,
    category: "actions",
    keywords: [
      "forge",
      "trace",
      "foundry",
      "test",
      "otel",
      "send",
      "span",
      "anvil",
    ],
  },
  {
    id: "action-invite-member",
    label: "Invite teammate",
    description: "Invite someone to your organization",
    icon: UserPlus,
    category: "actions",
    keywords: [
      "invite",
      "member",
      "teammate",
      "add",
      "user",
      "people",
      "organization",
      "team",
    ],
  },
  {
    id: "action-feature-flags",
    label: "Feature Flags (Dev)",
    description: "Toggle feature flag overrides for this browser",
    icon: ToggleLeft,
    category: "actions",
    keywords: [
      "feature",
      "flag",
      "flags",
      "toggle",
      "dev",
      "override",
      "experiment",
      "release",
    ],
  },
];

/**
 * Support/help commands that open external links.
 */
export const supportCommands: Command[] = [
  {
    id: "support-plans",
    label: "View Plans",
    description: "Manage subscription or license",
    icon: CreditCard,
    category: "actions",
    keywords: [
      "plan",
      "upgrade",
      "subscription",
      "billing",
      "license",
      "pricing",
    ],
    // Path is set dynamically in useFilteredCommands based on IS_SAAS
  },
  {
    id: "action-open-chat",
    label: "Open Chat",
    description: "Chat with support",
    icon: MessageCircle,
    category: "actions",
    keywords: ["help", "support", "chat", "crisp", "contact"],
  },
  {
    id: "action-docs",
    label: "Documentation",
    description: "Open LangWatch docs",
    icon: BookOpen,
    category: "actions",
    keywords: ["docs", "help", "guide", "manual", "documentation"],
    externalUrl: "https://docs.langwatch.ai",
  },
  {
    id: "action-github",
    label: "GitHub Support",
    description: "Get help on GitHub",
    icon: LifeBuoy,
    category: "actions",
    keywords: ["github", "support", "help", "community"],
    externalUrl:
      "https://github.com/orgs/langwatch/discussions/categories/support",
  },
  {
    id: "action-discord",
    label: "Discord",
    description: "Join our Discord community",
    icon: MessageCircle,
    category: "actions",
    keywords: ["discord", "community", "chat", "help"],
    externalUrl: "https://discord.gg/kT4PhDS2gH",
  },
  {
    id: "action-status",
    label: "Status Page",
    description: "Check system status",
    icon: Activity,
    category: "actions",
    keywords: ["status", "uptime", "health", "outage"],
    externalUrl: "https://status.langwatch.ai/",
  },
  {
    id: "action-feature-request",
    label: "Feature Request",
    description: "Request a new feature",
    icon: Lightbulb,
    category: "actions",
    keywords: ["feature", "request", "idea", "suggestion"],
    externalUrl:
      "https://github.com/orgs/langwatch/discussions/categories/ideas",
  },
  {
    id: "action-bug-report",
    label: "Report a Bug",
    description: "Report an issue",
    icon: Bug,
    category: "actions",
    keywords: ["bug", "issue", "report", "problem", "error"],
    externalUrl: "https://github.com/langwatch/langwatch/issues",
  },
];

/**
 * Theme switching commands.
 */
export const themeCommands: Command[] = [
  {
    id: "action-theme-light",
    label: "Light Theme",
    description: "Switch to light mode",
    icon: Sun,
    category: "actions",
    keywords: ["theme", "light", "mode", "bright", "day"],
  },
  {
    id: "action-theme-dark",
    label: "Dark Theme",
    description: "Switch to dark mode",
    icon: Moon,
    category: "actions",
    keywords: ["theme", "dark", "mode", "night"],
  },
  {
    id: "action-theme-system",
    label: "System Theme",
    description: "Use system preference",
    icon: Monitor,
    category: "actions",
    keywords: ["theme", "system", "auto", "default"],
  },
];

/**
 * Top-level navigation command IDs (shown by default).
 */
const topLevelNavIds = new Set([
  "nav-home",
  "nav-analytics",
  "nav-traces",
  "nav-traces-v2",
  "nav-agent-testing",
  "nav-simulations",
  "nav-online-evaluations",
  "nav-experiments",
  "nav-annotations",
  "nav-prompts",
  "nav-agents",
  "nav-workflows",
  "nav-evaluators",
  "nav-datasets",
  "nav-automations",
  "nav-settings",
]);

/**
 * Top-level navigation commands (shown when query is empty).
 */
export const topLevelNavigationCommands: Command[] = navigationCommands.filter(
  (cmd) => topLevelNavIds.has(cmd.id),
);

/**
 * All static commands combined.
 */
export const allStaticCommands: Command[] = [
  ...navigationCommands,
  ...actionCommands,
  ...supportCommands,
  ...themeCommands,
];

/**
 * Values of the release flags the command list reads, as resolved for the
 * person using it. A flag missing from the map has not answered yet.
 */
export type CommandFeatureFlagValues = Partial<
  Record<FrontendFeatureFlag, boolean>
>;

/**
 * Drops the commands whose release flag does not hold the value they need.
 * A command with no flag is always offered, and a flag that has not answered
 * yet keeps its command out until it does, so the bar never lists two routes
 * to the same work while the flag is in flight.
 */
export function filterCommandsByFeatureFlags({
  commands,
  flags,
}: {
  commands: Command[];
  flags: CommandFeatureFlagValues;
}): Command[] {
  return commands.filter((command) => {
    if (!command.featureFlag) return true;
    return flags[command.featureFlag.flag] === command.featureFlag.enabled;
  });
}

/**
 * Filter commands by query string.
 */
export function filterCommands(commands: Command[], query: string): Command[] {
  if (!query.trim()) return commands;

  const lowerQuery = query.toLowerCase();
  return commands.filter((cmd) => {
    const labelMatch = cmd.label.toLowerCase().includes(lowerQuery);
    const descriptionMatch = cmd.description
      ?.toLowerCase()
      .includes(lowerQuery);
    const keywordMatch = cmd.keywords?.some((kw) =>
      kw.toLowerCase().includes(lowerQuery),
    );
    return labelMatch || descriptionMatch || keywordMatch;
  });
}
